import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Ruler, QrCode, Truck } from "lucide-react";

export default function Collars() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [collars, setCollars] = useState([]);
  const [pets, setPets] = useState([]);
  const [buyOpen, setBuyOpen] = useState(false);
  const [sel, setSel] = useState(null);
  const [petSel, setPetSel] = useState("");
  const [phone, setPhone] = useState("");
  const [neck, setNeck] = useState("");
  const [addr, setAddr] = useState({ cep: "", rua: "", numero: "", complemento: "", bairro: "", cidade: "Joinville" });
  const [buying, setBuying] = useState(false);

  useEffect(() => {
    api.get("/collars").then((r) => setCollars(r.data));
  }, []);

  useEffect(() => {
    if (user) api.get("/pets").then((r) => setPets(r.data)).catch(() => {});
  }, [user]);

  const openBuy = (c) => {
    if (!user) return navigate("/login?next=/coleiras");
    setSel(c);
    setBuyOpen(true);
  };

  const confirm = async () => {
    if (!addr.cep || !addr.rua || !addr.numero) return toast.error("Informe CEP, rua e número para a entrega");
    if (sel.material === "couro") {
      if (!petSel) return toast.error("Escolha o pet para a gravação");
      if (!phone) return toast.error("Informe o telefone que será gravado na coleira");
      if (!neck) return toast.error("Informe a medida do pescoço do seu pet");
    }
    setBuying(true);
    try {
      const r = await api.post("/payments/checkout", {
        kind: "collar", collar_id: sel.collar_id, pet_id: petSel || null,
        neck_cm: neck ? Number(neck) : null, phone, address: addr,
      });
      if (r.data.init_point) { window.location.assign(r.data.init_point); return; }
      toast.success(
        sel.material === "couro"
          ? "Pedido aprovado! Vamos gravar o nome do pet, seu telefone e o QR code na coleira. Avisaremos quando a confecção começar."
          : "Pedido aprovado! Avisaremos quando sua coleira for enviada.",
        { duration: 7000 }
      );
      setBuyOpen(false);
      navigate("/app");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro no checkout");
    } finally {
      setBuying(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-12" data-testid="collars-page">
      <div className="text-center max-w-2xl mx-auto mb-10">
        <h1 className="text-3xl sm:text-4xl font-bold font-heading text-brand-graphite">Coleiras PetMachine</h1>
        <p className="text-muted-foreground mt-3">
          Escolha o modelo e receba no seu endereço. Nas coleiras de couro, gravamos a laser o{" "}
          <strong className="text-brand-graphite">nome do pet + seu telefone + QR code</strong> da tag —
          identificação completa direto na coleira.
        </p>
      </div>

      {collars.length === 0 ? (
        <p className="text-center text-muted-foreground py-16" data-testid="no-collars">Novidades em breve!</p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {collars.map((c) => (
            <div key={c.collar_id} className="bg-white rounded-2xl overflow-hidden border card-hover flex flex-col" data-testid={`collar-card-${c.collar_id}`}>
              <div className="relative h-48 bg-brand-mintLight">
                {c.image_url && <img src={c.image_url} alt={c.name} className="w-full h-full object-cover" />}
                <span className={`absolute top-3 left-3 text-xs font-semibold px-2.5 py-1 rounded-full ${c.material === "couro" ? "bg-brand-coral text-white" : "bg-brand-mint text-white"}`}>
                  {c.material === "couro" ? "Couro • gravação inclusa" : "Corrente • pronta entrega"}
                </span>
              </div>
              <div className="p-5 flex flex-col flex-1">
                <h3 className="font-heading font-semibold text-lg text-brand-graphite">{c.name}</h3>
                {c.material === "couro" && (
                  <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                    <QrCode className="w-3 h-3 text-brand-coral" /> Gravação: nome do pet + telefone + QR code
                  </p>
                )}
                <p className="font-heading font-bold text-xl text-brand-coral mt-2">R$ {Number(c.price).toFixed(2).replace(".", ",")}</p>
                <Button onClick={() => openBuy(c)} className="w-full rounded-full mt-4 bg-brand-coral hover:bg-brand-coralDark text-white" data-testid={`collar-buy-${c.collar_id}`}>
                  <Truck className="w-4 h-4 mr-2" /> Comprar com entrega
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={buyOpen} onOpenChange={setBuyOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{sel?.material === "couro" ? "Personalizar e comprar" : "Finalizar compra"} — {sel?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3" data-testid="collar-order-form">
            {sel?.material === "couro" && (
              <>
                <div>
                  <Label>Gravar para qual pet? *</Label>
                  <Select value={petSel} onValueChange={setPetSel}>
                    <SelectTrigger className="mt-1" data-testid="collar-pet-select"><SelectValue placeholder="Escolha o pet" /></SelectTrigger>
                    <SelectContent>
                      {pets.map((p) => <SelectItem key={p.pet_id} value={p.pet_id}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Seu telefone para gravar *</Label><Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(47) 99999-9999" className="mt-1" data-testid="collar-phone-input" /></div>
                <div className="bg-brand-mustardLight rounded-xl p-3 text-xs text-brand-graphite" data-testid="measure-guide">
                  <p className="font-semibold flex items-center gap-1.5 mb-1"><Ruler className="w-3.5 h-3.5" /> Como medir o pescoço do seu pet</p>
                  Use uma fita métrica (ou um barbante + régua) ao redor do pescoço, na altura onde a coleira fica, medindo de uma extremidade à outra. Não aperte: deixe a folga de dois dedos — nós já trabalhamos com essa folga na confecção.
                </div>
                <div><Label>Medida do pescoço (cm) *</Label><Input type="number" min="10" max="80" value={neck} onChange={(e) => setNeck(e.target.value)} placeholder="Ex: 35" className="mt-1" data-testid="collar-neck-input" /></div>
              </>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div><Label>CEP *</Label><Input value={addr.cep} onChange={(e) => setAddr({ ...addr, cep: e.target.value })} placeholder="89200-000" className="mt-1" data-testid="collar-addr-cep" /></div>
              <div><Label>Cidade</Label><Input value={addr.cidade} onChange={(e) => setAddr({ ...addr, cidade: e.target.value })} className="mt-1" data-testid="collar-addr-cidade" /></div>
            </div>
            <div><Label>Rua *</Label><Input value={addr.rua} onChange={(e) => setAddr({ ...addr, rua: e.target.value })} className="mt-1" data-testid="collar-addr-rua" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Número *</Label><Input value={addr.numero} onChange={(e) => setAddr({ ...addr, numero: e.target.value })} className="mt-1" data-testid="collar-addr-numero" /></div>
              <div><Label>Complemento</Label><Input value={addr.complemento} onChange={(e) => setAddr({ ...addr, complemento: e.target.value })} className="mt-1" /></div>
            </div>
            <div><Label>Bairro</Label><Input value={addr.bairro} onChange={(e) => setAddr({ ...addr, bairro: e.target.value })} className="mt-1" /></div>
            <Button onClick={confirm} disabled={buying} className="w-full bg-brand-coral hover:bg-brand-coralDark text-white rounded-full" data-testid="collar-confirm-btn">
              {buying ? "Processando..." : `Confirmar compra — R$ ${sel ? Number(sel.price).toFixed(2).replace(".", ",") : ""}`}
            </Button>
            <p className="text-xs text-center text-muted-foreground">Você receberá um aviso quando a coleira entrar em confecção.</p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
